import * as React from 'react';

import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import Module from 'bundles/promo-template/contentful/core/Module';
import { ArticlesCollectionModule } from 'bundles/promo-template/contentful/modules/ArticlesCollectionModule';
import { CannedCollectionModuleRenderer } from 'bundles/promo-template/contentful/modules/CannedCollectionModuleRenderer';
import { CitationModule } from 'bundles/promo-template/contentful/modules/CitationModule';
import FAQsModuleRenderer from 'bundles/promo-template/contentful/modules/FAQsModuleRenderer';
import HeroModuleRenderer from 'bundles/promo-template/contentful/modules/HeroModuleRenderer';
import InformationModuleRenderer from 'bundles/promo-template/contentful/modules/InformationModuleRenderer';
import { PartnerListModuleRenderer } from 'bundles/promo-template/contentful/modules/PartnerListModuleRenderer';
import { PromoBarModuleRenderer } from 'bundles/promo-template/contentful/modules/PromoBarModuleRenderer';
import { TestimonialsModuleRenderer } from 'bundles/promo-template/contentful/modules/TestimonialsModuleRenderer';
import ValuePropsModuleRenderer from 'bundles/promo-template/contentful/modules/ValuePropsModuleRenderer';
import CollectionsModule from 'bundles/promo-template/contentful/modules/native/CollectionsModule';
import type { Modules } from 'bundles/promo-template/queries/hooks/usePageContentQuery';

type Props = {
  modules: Modules;
  slug?: string;
  collectionsData?: CarouselCollection[];
  disableCrawling?: boolean;
};

const NUMBER_OF_MODULES_TO_PRELOAD = 3;

export default function ModuleRenderer({ modules, slug, collectionsData, disableCrawling }: Props) {
  return (
    <>
      {modules.map((module, i) => {
        let el;

        let removeTopPadding = false;
        let removeBottomPadding = false;
        let topMargin;

        if (i < modules.length - 1) {
          const nextModule = modules[i + 1];

          // if next module is set with stickToPrevious, then remove the bottom padding of current module
          if ('stickToPrevious' in nextModule && nextModule.stickToPrevious) {
            removeBottomPadding = true;
          }
        }

        if (i > 0) {
          const previousModule = modules[i - 1];
          const backgroundColorInPreviousModule =
            'backgroundColor' in previousModule ? previousModule.backgroundColor : 'white';
          const backgroundColorInCurrentModule = 'backgroundColor' in module ? module.backgroundColor : 'white';

          // if the adjacent modules have the same background color
          if (backgroundColorInCurrentModule === backgroundColorInPreviousModule) {
            // Remove top padding if the previous module is a not hero module,
            // and the current module has rounded corners
            if (previousModule.__typename !== 'PromoTemplateHero') {
              if (
                ('showRoundedCorners' in module && module.showRoundedCorners) ||
                ('showRoundedCorners' in previousModule && previousModule.showRoundedCorners)
              ) {
                removeTopPadding = false;
              } else {
                removeTopPadding = true;
              }
            }
          }

          // add top margin if current module has rounded corners and previous module has a background color
          if (
            'showRoundedCorners' in module &&
            module.showRoundedCorners &&
            backgroundColorInPreviousModule !== 'white'
          ) {
            topMargin = 'var(--cds-spacing-400)';
          }

          // if the module is set with stickToPrevious, then remove its top padding and the bottom padding of the previous module
          if ('stickToPrevious' in module && module.stickToPrevious) {
            removeTopPadding = true;

            if (module.__typename === 'PromoTemplateCannedCollection') {
              topMargin = 'var(--cds-spacing-200)';
            }
          }
        }

        const moduleConfig = {
          ...module,
          preloadImage: i < NUMBER_OF_MODULES_TO_PRELOAD,
          lazyLoadImage: i >= NUMBER_OF_MODULES_TO_PRELOAD,
          removeTopPadding,
          removeBottomPadding,
          topMargin,
        };

        switch (moduleConfig.__typename) {
          case 'PromoTemplateHero':
            el = <HeroModuleRenderer content={moduleConfig} slug={slug} />;
            break;
          case 'PromoTemplateCannedCollection':
            el = <CannedCollectionModuleRenderer content={moduleConfig} />;
            break;
          case 'PromoTemplateInformation':
            el = <InformationModuleRenderer content={moduleConfig} slug={slug} />;
            break;
          case 'PromoTemplateTestimonialCarousel':
            el = <TestimonialsModuleRenderer content={moduleConfig} />;
            break;
          case 'PromoTemplatePartnerList':
            el = <PartnerListModuleRenderer content={moduleConfig} />;
            break;
          case 'PromoTemplateValueProps':
            el = <ValuePropsModuleRenderer content={moduleConfig} />;
            break;
          case 'PromoTemplateCollections':
            el = (
              <CollectionsModule
                content={moduleConfig}
                collectionsData={collectionsData}
                disableCrawling={disableCrawling}
              />
            );
            break;
          case 'PromoTemplateArticlesCollection':
            el = <ArticlesCollectionModule content={moduleConfig} />;
            break;
          case 'PromoTemplateFaqs':
            el = <FAQsModuleRenderer content={moduleConfig} />;
            break;
          case 'PromoTemplateCitation':
            el = <CitationModule content={moduleConfig} />;
            break;
          case 'PromoTemplatePromoBar':
            el = <PromoBarModuleRenderer content={moduleConfig} />;
            break;
          default:
            return null;
        }

        return (
          <Module content={moduleConfig} key={module.sys.id}>
            <div data-testid={module.sys.id}>{el}</div>
          </Module>
        );
      })}
    </>
  );
}
